import { useEffect } from 'react';

import { cx } from '@emotion/css';
import { Col } from 'antd';
import { isUndefined } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { doCreateOrder } from 'src/actions';
import { useSubmittedAccountSelector } from 'src/hooks';
import {
  MModalBody,
  MModalFooter,
  MButton,
  MFormTextField,
  MModalTitle,
  MTooltip,
  MTypography,
  CurrencyField,
} from 'src/lib';
import { mapOrderDurationDtoToLabel, mapOrderTypeDtoToLabel } from 'src/mappers';
import { SecurityOrderView, NewTradeQuoteView } from 'src/models';
import { BaseStyle, AlignItems, JustifyContent, Display, Spacing, ColorConstant } from 'src/styles';

interface TradeSecurityHoldingReviewStepProps {
  quote: NewTradeQuoteView;
  quantity?: number;
  order: SecurityOrderView;
  onClose: () => void;
  onBack: () => void;
  onServiceFee: () => void;
  onLastPrice: () => void;
  onReadMore: () => void;
  onSuccess?: () => void;
  onError?: () => void;
}

export const SecurityHoldingTradeReviewStep = ({
  quote,
  quantity,
  order,
  onBack,
  onClose,
  onServiceFee,
  onLastPrice,
  onReadMore,
  onSuccess,
  onError,
}: TradeSecurityHoldingReviewStepProps) => {
  const dispatch = useDispatch();
  const { account } = useSubmittedAccountSelector();

  const isSuccess = useSelector((state: any) => Boolean(state.orders.create.__succeeded));
  const isLoading = useSelector((state: any) => Boolean(state.orders.create.isLoading));
  const isError = useSelector((state: any) => Boolean(state.orders.create.__failed));

  const onSubmit = () => {
    dispatch(
      doCreateOrder({
        params: {
          accountId: account.accountId,
        },
        body: {
          action: order.action,
          duration: order.duration,
          orderType: order.type,
          price: order.price,
          quantity: order.quantity,
          stop: order.stop,
          symbol: quote.symbol,
        },
      }),
    );
  };

  useEffect(() => {
    if (isSuccess) {
      onSuccess?.();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      onError?.();
    }
  }, [isError]);

  return (
    <>
      <MModalTitle>Review Security {order.action} Order</MModalTitle>
      <MModalBody>
        <Col span={24} className={Spacing.my12} data-testid={'order-review-title'}>
          <MTypography variant='h2' align='center' color={ColorConstant.BRAND6} weight='semibold'>
            Review Order
          </MTypography>
        </Col>
        <Col span={24} className={cx(Spacing.mt12, Spacing.mb4)} data-testid={'order-review-subtitle'}>
          <MTypography variant='subtitle2' align='center'>
            If you experience difficulties, please contact{' '}
            <a href={`mailto:clientservices@myipo.com`}>client services</a>.
            <br />
            Execution price, speed and liquidity are affected by many factors, including market volatility, size, price,
            low volume, type of order and the exchange the shares are traded on. <a onClick={onReadMore}>Read more</a>.
          </MTypography>
        </Col>
        <Col span={24} className={cx(Spacing.mt4, Spacing.mb12)} data-testid={'order-review-disclaimer'}>
          <MTypography variant='subtitle2'>
            Intraday quotes are delayed up to 20 minutes, and do not reflect current bid, ask or last sale.
          </MTypography>
        </Col>
        <Col span={24}>
          <MFormTextField
            label='Security'
            value={`${quote.description} (${quote.symbol})`}
            testId='security-order-review-security'
          />
        </Col>
        <Col span={24}>
          <MFormTextField
            label='Order Type'
            value={order.type ? mapOrderTypeDtoToLabel(order.type) : undefined}
            testId='security-order-review-order-type'
          />
        </Col>
        <Col span={24}>
          <MFormTextField
            label={
              <>
                Last Price
                <MTooltip showIcon onClick={() => onLastPrice()} />
              </>
            }
            value={<CurrencyField value={quote.last} />}
            testId='security-order-review-last-price'
          />
        </Col>
        {!isUndefined(quantity) && (
          <Col span={24}>
            <MFormTextField label='Shares Held' value={quantity} testId='security-order-review-shares-held' />
          </Col>
        )}
        {order.price ? (
          <Col span={24}>
            <MFormTextField
              label='Limit Price'
              value={<CurrencyField value={order.price} />}
              testId='security-order-review-limit-price'
            />
          </Col>
        ) : null}
        {order.stop ? (
          <Col span={24}>
            <MFormTextField
              label='Stop Price'
              value={<CurrencyField value={order.stop} />}
              testId='security-order-review-stop-price'
            />
          </Col>
        ) : null}
        <Col span={24}>
          <MFormTextField
            label={`Shares to ${order.action}`}
            value={order.quantity}
            testId='security-order-review-shares-to-action'
          />
        </Col>
        <Col span={24}>
          <MFormTextField
            label='Order Expiration'
            value={order.duration ? mapOrderDurationDtoToLabel(order.duration) : undefined}
            testId='security-order-review-order-expiration'
          />
        </Col>
        <Col span={24}>
          <MFormTextField
            label={
              <>
                Service Fee
                <MTooltip showIcon onClick={() => onServiceFee()} />
              </>
            }
            value='$10.00'
            testId='security-order-review-service-fee'
          />
        </Col>
        <Col span={24}>
          <MFormTextField
            label='Estimated Total'
            value={<CurrencyField value={order.estimatedTotal} />}
            testId='security-order-review-estimated-total'
          />
        </Col>
      </MModalBody>
      <MModalFooter>
        <Col span={24} className={cx(Display.flex, AlignItems.center, JustifyContent.spaceBetween)}>
          <MButton type='tertiary' onClick={onClose} disabled={isLoading}>
            Close
          </MButton>
          <Col className={BaseStyle.levelRight}>
            <MButton type='secondary' onClick={onBack} disabled={isLoading}>
              Back
            </MButton>
            <MButton onClick={onSubmit} loading={isLoading}>
              Submit
            </MButton>
          </Col>
        </Col>
      </MModalFooter>
    </>
  );
};
